import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Card, CardMedia, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';

import { FavUnfavIcon, GoBackButton } from '@app/components';
import { SvgColor } from '@app/components/svg-color';
import useResponsive from '@app/hooks/use-responsive';
import { TruncatedTypography } from '@app/components/typography';
import { IsMyMealIcon } from '@app/components/IsMyMealIcon';
import { saveViewedRecipe } from '@app/modules/common';
import { IngredientsTab } from './ingredients-tab';
import { NutritionTab } from './nutrition-tab';

const RecipeDetailsScreen = () => {
  const { state: item } = useLocation();
  const dispatch = useDispatch();
  const isMobile = useResponsive('down', 'sm');
  const theme = useTheme();

  const tabs = ['Ingredients', 'Nutrition'];

  const [value, setValue] = useState(tabs[0]);
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const timeCaloriesRatings = [
    {
      prop: item.totalTime,
      icon: `/assets/icons/ic_time.svg`,
    },
    {
      prop: parseFloat(item?.nutrition?.calories).toFixed(2) || item.calories,
      icon: `/assets/icons/ic_fire.svg`,
    },
    {
      prop: item?.ratings || 'No Rating',
      icon: `/assets/icons/ic_star.svg`,
    },
  ];

  useEffect(() => {
    dispatch(saveViewedRecipe(item));
  }, [item, dispatch]);

  return (
    <Stack>
      <Stack sx={{ flexDirection: isMobile ? '' : 'row', justifyContent: 'space-between', gap: 2 }}>
        <GoBackButton sx={{ width: 40, height: 40 }} />
        <Stack sx={[styles.imageContainer, { width: isMobile ? '100%' : '40%' }]}>
          <CardMedia
            component="img"
            image={item?.image}
            sx={[styles.image, { height: isMobile ? '200px' : '300px' }]}
          />
          <FavUnfavIcon id={item?.uri} style={styles.favIcon} />
          <IsMyMealIcon item={item} style={{ ...styles.favIcon, top: 50 }} />
        </Stack>
        <Card
          sx={[
            styles.detailsContainer,
            {
              width: isMobile ? '100%' : '57%',
            },
          ]}
        >
          <Stack sx={{ flexDirection: 'row' }}>
            <TruncatedTypography fontSize={18} fontWeight={'bold'} text={item?.label} />
            {/* <FavUnfavIcon id={item?.uri} style={styles.favIcon} /> */}
          </Stack>
          <Typography
            fontSize={12}
            color={theme.palette.tertiary.shadyText}
            sx={styles.description}
          >
            {item?.label}
          </Typography>
          <Stack sx={[styles.timeCaloriesRatingsContainer, { width: isMobile ? '100%' : '80%' }]}>
            {timeCaloriesRatings.map((timeCalorieRating, index) => (
              <Stack
                key={index}
                sx={[
                  styles.timeCaloriesRatingsView,
                  { borderRight: index !== 2 ? '1px  solid lightGrey' : 0 },
                ]}
              >
                <SvgColor
                  src={timeCalorieRating.icon}
                  color={theme.palette.primary.main}
                  fontWeight={'bold'}
                />
                <Typography sx={styles.timeCaloriesRatingsLabel}>
                  {timeCalorieRating.prop}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>

        {/* ------------------ */}
      </Stack>
      <Card sx={{ mt: 2, borderRadius: '10px' }}>
        <Tabs
          sx={{ px: 5, mb: 1 }}
          value={value}
          onChange={handleChange}
          textColor="primary"
          indicatorColor="primary"
          TabIndicatorProps={{ sx: { height: 4, borderRadius: '50px' } }}
          aria-label="secondary tabs example"
        >
          {tabs?.map((screen) => <Tab value={screen} label={screen} />)}
        </Tabs>
        {value === 'Ingredients' && <IngredientsTab data={item.ingredients} />}
        {value === 'Nutrition' && <NutritionTab data={item.nutrition} />}
      </Card>
    </Stack>
  );
};
const styles = {
  imageContainer: { position: 'relative', borderRadius: '10px' },
  favIcon: {
    position: 'absolute' as const,
    top: '10px',
    right: '5px',
    color: 'red',
    backgroundColor: 'white',
    height: '30px',
    width: '30px',
  },
  image: { borderRadius: '10px' },
  detailsContainer: {
    p: 2,
    borderRadius: '10px',
    position: 'relative',
    height: '300px',
  },
  timeCaloriesRatingsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: '#E9E4E2',
    borderRadius: '50px',
    padding: '10px',
    marginTop: '30px',
    height: '80px',
  },
  timeCaloriesRatingsView: {
    alignItems: 'center',
    width: '33%',
  },
  timeCaloriesRatingsLabel: { fontSize: '14px', mt: 1 },
  description: {
    overflow: 'hidden',
    letterSpacing: 1,
    mt: 2,
    height: '110px',
  },
};
export { RecipeDetailsScreen };
